import React, { useState, useEffect } from "react";
import Sidebar from "../Components/SideBar";
import { useSidebar } from "../SidebarContext";

const MOBILE_MAX = 767;
const TABLET_MAX = 1023;

const StaffShell = ({ children, hideSidebar }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    isMobile: window.innerWidth <= MOBILE_MAX,
    isTablet: window.innerWidth > MOBILE_MAX && window.innerWidth <= TABLET_MAX,
  }));
  const { setIsSidebarOpen, setIsMobile } = useSidebar();

  const { isMobile, isTablet } = viewport;
  const isOverlay = isMobile || isTablet;

  useEffect(() => {
    document.body.classList.toggle("sidebar-mobile-open", mobileOpen && isOverlay);
    return () => document.body.classList.remove("sidebar-mobile-open");
  }, [mobileOpen, isOverlay]);

  useEffect(() => {
    if (hideSidebar) return undefined;
    document.documentElement.setAttribute("data-admin-theme", "dark");
    return () => document.documentElement.removeAttribute("data-admin-theme");
  }, [hideSidebar]);

  useEffect(() => {
    const syncViewport = () => {
      const w = window.innerWidth;
      setViewport({
        isMobile: w <= MOBILE_MAX,
        isTablet: w > MOBILE_MAX && w <= TABLET_MAX,
      });
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  useEffect(() => {
    if (isOverlay) {
      setCollapsed(false);
      setMobileOpen(false);
    } else {
      setCollapsed(true);
      setMobileOpen(false);
    }
  }, [isOverlay]);

  useEffect(() => {
    setIsMobile(isOverlay);
    setIsSidebarOpen(isOverlay ? mobileOpen : !collapsed);
  }, [collapsed, mobileOpen, isOverlay, setIsMobile, setIsSidebarOpen]);

  const closeMobileMenu = () => setMobileOpen(false);
  const sidebarWidth = isOverlay ? 0 : collapsed ? 72 : 280;

  if (hideSidebar) {
    return <div className="min-h-screen">{children}</div>;
  }

  return (
    <div className="min-h-screen" data-admin-theme="dark">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        isTablet={isTablet}
        onCloseMobile={closeMobileMenu}
      />

      <main
        className="transition-all duration-300 ease-in-out"
        style={{
          marginLeft: isOverlay ? 0 : `${sidebarWidth}px`,
          paddingTop: isOverlay ? "calc(3.25rem + env(safe-area-inset-top, 0px))" : 0,
          pointerEvents: mobileOpen && isOverlay ? "none" : "auto",
        }}
        onClick={isOverlay ? closeMobileMenu : undefined}
      >
        <div className="p-2 sm:p-3 md:p-4">
          {children}
          <span className="admin-page-bottom-span" aria-hidden="true" />
        </div>
      </main>

      {mobileOpen && isOverlay && (
        <div
          className="fixed inset-0 bg-black/55 z-[125] backdrop-blur-[2px]"
          onClick={closeMobileMenu}
          aria-hidden
        />
      )}
    </div>
  );
};

export default StaffShell;
